import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Alert
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Article as ArticleIcon,
  ViewModule as ContentIcon,
  Category as CategoryIcon,
  LocalOffer as OfferIcon,
  Group as GroupIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UnfoldMore as UnfoldMoreIcon,
  Visibility as VisibilityIcon,
  AttachMoney as AttachMoneyIcon,
  ExpandMore,
  ExpandLess,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import DateRangeFilter, { getPresetDates } from '../components/DateRangeFilter';

// Modern Table Styles based on UI Foundations Kit Simple Table
const tableStyles = {
  container: {
    borderRadius: 2,
    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    border: 'none',
    overflow: 'hidden',
  },
  headerRow: {
    backgroundColor: '#FAFAFA',
  },
  headerCell: {
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E5E7EB',
    py: 1.5,
    px: 1,
  },
  sortableHeaderCell: {
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E5E7EB',
    py: 1.5,
    px: 1,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  bodyRow: {
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  bodyRowAlternate: {
    backgroundColor: '#FFFFFF',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  bodyCell: {
    borderBottom: '1px solid #F3F4F6',
    py: 1.5,
    px: 1,
    fontSize: '0.8rem',
    color: '#111827',
  },
  groupRow: {
    backgroundColor: '#F3F4F6',
    borderBottom: '1px solid #E5E7EB',
  },
  emptyRow: {
    py: 6,
    textAlign: 'center',
  },
};

// Nested table styles for page breakdowns
const nestedTableStyles = {
  container: {
    mt: 0,
    mb: 0,
    border: 'none',
    backgroundColor: 'transparent',
  },
  headerCell: {
    fontWeight: 600,
    color: '#6B7280',
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E5E7EB',
    py: 1,
    px: 1,
    backgroundColor: '#FAFAFA',
  },
  bodyCell: {
    borderBottom: '1px solid #F3F4F6',
    py: 1,
    px: 1,
    fontSize: '0.8rem',
    color: '#374151',
  },
  snakeRow: {
    backgroundColor: '#FEF3C7',
  },
};

// Mock Chart Component
const MockChart = ({ title, type = 'line', height = 300 }) => {
  const generateRandomData = () => {
    return Array.from({ length: 12 }, () => Math.floor(Math.random() * 100));
  };

  const data = generateRandomData();
  const maxValue = Math.max(...data);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ height: height, display: 'flex', alignItems: 'end', gap: 1, p: 2 }}>
          {type === 'bar' ? (
            data.map((value, index) => (
              <Box
                key={index}
                sx={{
                  height: `${(value / maxValue) * 100}%`,
                  width: '100%',
                  backgroundColor: 'primary.main',
                  opacity: 0.7,
                  borderRadius: '4px 4px 0 0',
                  minHeight: '4px'
                }}
              />
            ))
          ) : (
            <svg width="100%" height="100%" viewBox="0 0 300 200">
              <polyline
                fill="none"
                stroke="#3B82F6"
                strokeWidth="3"
                points={data.map((value, index) =>
                  `${(index / (data.length - 1)) * 300},${200 - (value / maxValue) * 180}`
                ).join(' ')}
              />
              {data.map((value, index) => (
                <circle
                  key={index}
                  cx={(index / (data.length - 1)) * 300}
                  cy={200 - (value / maxValue) * 180}
                  r="4"
                  fill="#3B82F6"
                />
              ))}
            </svg>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Mock Pie Chart Component
const MockPieChart = ({ title, data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ position: 'relative', width: 200, height: 200 }}>
            <svg width="200" height="200" viewBox="0 0 200 200">
              {data.map((item, index) => {
                const percentage = (item.value / total) * 100;
                const angle = (item.value / total) * 360;
                const startAngle = currentAngle;
                const endAngle = currentAngle + angle;
                currentAngle += angle;

                const x1 = 100 + 80 * Math.cos((startAngle - 90) * Math.PI / 180);
                const y1 = 100 + 80 * Math.sin((startAngle - 90) * Math.PI / 180);
                const x2 = 100 + 80 * Math.cos((endAngle - 90) * Math.PI / 180);
                const y2 = 100 + 80 * Math.sin((endAngle - 90) * Math.PI / 180);
                const largeArc = angle > 180 ? 1 : 0;

                return (
                  <path
                    key={index}
                    d={`M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`}
                    fill={item.color}
                    opacity="0.8"
                  />
                );
              })}
            </svg>
          </Box>
          <Box>
            {data.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: item.color,
                    borderRadius: '2px'
                  }}
                />
                <Typography variant="body2">
                  {item.label}: {total > 0 ? `${((item.value / total) * 100).toFixed(1)}%` : '-'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Reporting = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [adsenseSuccess, setAdsenseSuccess] = useState(false);
  const [adsensePingResult, setAdsensePingResult] = useState(null);
  const [adsensePingLoading, setAdsensePingLoading] = useState(false);
  const [adsenseGlobalConnected, setAdsenseGlobalConnected] = useState(false);
  const [adsenseGlobalEmail, setAdsenseGlobalEmail] = useState(null);
  const [checkingAdsenseStatus, setCheckingAdsenseStatus] = useState(true);

  // Check initial AdSense global status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await api.get('/adsense/status');
        setAdsenseGlobalConnected(res.data.connected);
        if (res.data.email) {
          setAdsenseGlobalEmail(res.data.email);
        }
      } catch (err) {
        console.error('Failed to check AdSense status:', err);
      } finally {
        setCheckingAdsenseStatus(false);
      }
    };
    checkStatus();
  }, []);

  // Handle AdSense callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('adsense') === 'success') {
      setAdsenseSuccess(true);
      setActiveTab(5); // Switch to Platform settings tab
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Mock data for other tabs (not Offer Stats)


  const affiliateStatsData = {
    totalAffiliates: 89,
    activeAffiliates: 67,
    topPerformers: [
      { name: 'john@email.com', commissions: '$2,450', conversions: 45 },
      { name: 'sarah@email.com', commissions: '$1,890', conversions: 38 },
      { name: 'mike@email.com', commissions: '$1,650', conversions: 32 },
      { name: 'lisa@email.com', commissions: '$1,320', conversions: 28 },
      { name: 'alex@email.com', commissions: '$980', conversions: 22 }
    ]
  };

  // Blog Stats - Real state
  const [blogStats, setBlogStats] = useState(null);
  const [blogStatsLoading, setBlogStatsLoading] = useState(true);
  const [blogStatsError, setBlogStatsError] = useState('');
  const [expandedBlogs, setExpandedBlogs] = useState({});
  const [expandedAffiliates, setExpandedAffiliates] = useState({});
  const [blogPreset, setBlogPreset] = useState('thisMonth');
  const [blogDateDates, setBlogDateDates] = useState(getPresetDates('thisMonth'));
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogSelectedCategory, setBlogSelectedCategory] = useState('');
  const [blogSelectedSubcategory, setBlogSelectedSubcategory] = useState('');
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState('');
  const [viewAllAffiliates, setViewAllAffiliates] = useState(true);
  const [expandedSnakeBlogs, setExpandedSnakeBlogs] = useState({});

  // dateRanges replaced by DateRangeFilter component

  const pieChartData = [
    { label: 'Active', value: 65, color: '#4caf50' },
    { label: 'Expired', value: 20, color: '#f44336' },
    { label: 'Draft', value: 10, color: '#ff9800' },
    { label: 'Pending', value: 5, color: '#9e9e9e' }
  ];

  const categoryData = [
    { label: 'Technology', value: 35, color: '#3B82F6' },
    { label: 'Fashion', value: 25, color: '#eabbc3' },
    { label: 'Health', value: 20, color: '#4caf50' },
    { label: 'Travel', value: 12, color: '#ff9800' },
    { label: 'Food', value: 8, color: '#9c27b0' }
  ];

  // State for Offer Stats
  const [offerStatsData, setOfferStatsData] = useState([]);
  const [offerStatsLoading, setOfferStatsLoading] = useState(true);
  const [offerStatsSummary, setOfferStatsSummary] = useState({
    totalClicks: 0,
    totalConversions: 0,
    totalRevenue: 0,
    averageCPA: 0
  });
  const [filterOptions, setFilterOptions] = useState({
    networks: [],
    brands: [],
    offers: []
  });

  // Filter states
  const [offerPreset, setOfferPreset] = useState('thisMonth');
  const [filters, setFilters] = useState({
    ...getPresetDates('thisMonth'),
    networkCompany: '',
    brandName: '',
    offerId: ''
  });

  // Sorting states
  const [sortColumn, setSortColumn] = useState('totalPayout');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  const [blogSortColumn, setBlogSortColumn] = useState('totalRevenue');
  const [blogSortDirection, setBlogSortDirection] = useState('desc');

  const [snakeSortColumn, setSnakeSortColumn] = useState('revenue');
  const [snakeSortDirection, setSnakeSortDirection] = useState('desc');
  const [snakeAdminRevExpanded, setSnakeAdminRevExpanded] = useState(false);

  // Fetch offer stats data
  const fetchOfferStats = async (customFilters = null) => {
    setOfferStatsLoading(true);
    try {
      // Use custom filters if provided, otherwise use state filters
      const activeFilters = customFilters || filters;

      const params = new URLSearchParams({
        start_date: activeFilters.startDate,
        end_date: activeFilters.endDate
      });

      if (activeFilters.networkCompany) params.append('network_company', activeFilters.networkCompany);
      if (activeFilters.brandName) params.append('brand_name', activeFilters.brandName);
      if (activeFilters.offerId) params.append('offer_id', activeFilters.offerId);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/affiliate/admin/offer-stats?${params}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        console.log('✅ Offer stats received:', response.data);
        setOfferStatsData(response.data.stats);
        setOfferStatsSummary(response.data.summary);
        setFilterOptions(response.data.filters);
      } else {
        console.error('❌ API returned success:false', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching offer stats:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
    } finally {
      setOfferStatsLoading(false);
    }
  };

  // Fetch data when filters change
  useEffect(() => {
    if (activeTab === 0) {
      fetchOfferStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters]);

  // Load categories for Blog Stats
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await api.get('/categories/select');
        setBlogCategories(response.data || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load affiliates for admin
  useEffect(() => {
    const loadAffiliates = async () => {
      try {
        const response = await api.get('/affiliates');
        setAffiliates(response.data || []);
      } catch (error) {
        console.error('Error loading affiliates:', error);
      }
    };
    loadAffiliates();
  }, []);

  // Fetch Blog Stats
  useEffect(() => {
    const fetchBlogStats = async () => {
      if (activeTab !== 2 && activeTab !== 3) return; // Fetch for both Blog Stats and Snake Stats tabs
      if (!blogDateDates.startDate || !blogDateDates.endDate) return; // Wait for dates

      setBlogStatsLoading(true);
      setBlogStatsError('');
      try {
        const targetAffiliateId = !viewAllAffiliates && selectedAffiliateId
          ? selectedAffiliateId
          : 'all';

        const params = new URLSearchParams({
          startDate: new Date(blogDateDates.startDate + 'T00:00:00').toISOString(),
          endDate: new Date(blogDateDates.endDate + 'T23:59:59').toISOString()
        });

        if (blogSelectedCategory) {
          params.append('categoryId', blogSelectedCategory);
        }
        if (blogSelectedSubcategory) {
          params.append('subcategoryId', blogSelectedSubcategory);
        }
        if (viewAllAffiliates) {
          params.append('viewAll', 'true');
        }

        console.log('Fetching blog performance stats for affiliate:', targetAffiliateId);

        const response = await api.get(`/affiliate/blog-performance/${targetAffiliateId}?${params}`);

        console.log('Blog performance stats response:', response.data);
        setBlogStats(response.data?.stats || null);
      } catch (error) {
        console.error('Error fetching blog stats:', error);
        setBlogStatsError('Failed to load blog performance data');
        setBlogStats(null);
      } finally {
        setBlogStatsLoading(false);
      }
    };

    fetchBlogStats();
  }, [activeTab, blogDateDates, blogSelectedCategory, blogSelectedSubcategory, selectedAffiliateId, viewAllAffiliates]);

  // Toggle blog expansion
  const handleToggleBlog = (blogPostId) => {
    setExpandedBlogs(prev => ({
      ...prev,
      [blogPostId]: !prev[blogPostId]
    }));
  };

  // Toggle affiliate expansion
  const handleToggleAffiliate = (affiliateId) => {
    setExpandedAffiliates(prev => ({
      ...prev,
      [affiliateId]: !prev[affiliateId]
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };



  const handleClearFilters = () => {
    const clearedFilters = {
      ...filters,
      networkCompany: '',
      brandName: '',
      offerId: ''
    };
    setFilters(clearedFilters);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('desc'); // Default to desc for numeric columns
    }
  };

  const handleBlogSort = (column) => {
    if (blogSortColumn === column) {
      setBlogSortDirection(blogSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setBlogSortColumn(column);
      setBlogSortDirection('desc');
    }
  };

  const handleSnakeSort = (column) => {
    if (snakeSortColumn === column) {
      setSnakeSortDirection(snakeSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSnakeSortColumn(column);
      setSnakeSortDirection('desc');
    }
  };

  // Sort the offer stats data
  const getSortedData = () => {
    if (!sortColumn) return offerStatsData;

    return [...offerStatsData].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'networkCompany':
          aValue = (a.networkCompany || '').toLowerCase();
          bValue = (b.networkCompany || '').toLowerCase();
          break;
        case 'brandName':
          aValue = (a.brandName || '').toLowerCase();
          bValue = (b.brandName || '').toLowerCase();
          break;
        case 'offerTitle':
          aValue = (a.offerTitle || '').toLowerCase();
          bValue = (b.offerTitle || '').toLowerCase();
          break;
        case 'clicks':
          aValue = a.clicks || 0;
          bValue = b.clicks || 0;
          break;
        case 'conversions':
          aValue = a.conversions || 0;
          bValue = b.conversions || 0;
          break;
        case 'cpa':
          aValue = a.cpa || 0;
          bValue = b.cpa || 0;
          break;
        case 'totalPayout':
          aValue = a.totalPayout || 0;
          bValue = b.totalPayout || 0;
          break;
        default:
          return 0;
      }

      const comparison = typeof aValue === 'string'
        ? aValue.localeCompare(bValue)
        : aValue - bValue;

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const renderSortIcon = (column, currentSortColumn, currentSortDirection) => {
    // For backward compatibility with simpler calls
    const targetColumn = currentSortColumn !== undefined ? currentSortColumn : sortColumn;
    const targetDirection = currentSortDirection !== undefined ? currentSortDirection : sortDirection;

    if (targetColumn !== column) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.3 }} />;
    }
    return targetDirection === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };

  const renderOfferStats = () => (
    <Box>
      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <DateRangeFilter
                label="Date Range"
                value={offerPreset}
                size="small"
                onChange={({ startDate, endDate }, preset) => {
                  setOfferPreset(preset);
                  handleFilterChange('startDate', startDate);
                  handleFilterChange('endDate', endDate);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Network / Company</InputLabel>
                <Select
                  value={filters.networkCompany}
                  onChange={(e) => handleFilterChange('networkCompany', e.target.value)}
                  label="Network / Company"
                  displayEmpty
                  notched
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.networks.map((network) => (
                    <MenuItem key={network} value={network}>
                      {network}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Brand Name</InputLabel>
                <Select
                  value={filters.brandName}
                  onChange={(e) => handleFilterChange('brandName', e.target.value)}
                  label="Brand Name"
                  displayEmpty
                  notched
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.brands.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      {brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Offer</InputLabel>
                <Select
                  value={filters.offerId}
                  onChange={(e) => handleFilterChange('offerId', e.target.value)}
                  label="Offer"
                  displayEmpty
                  notched
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.offers.map((offer) => (
                    <MenuItem key={offer.id} value={offer.id}>
                      {offer.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  disabled={offerStatsLoading}
                  sx={{ minWidth: 120 }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {/* Summary Cards moved to table rows */}

      {/* Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Offer Performance Report
          </Typography>
          {offerStatsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ ...tableStyles.container, mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={tableStyles.headerRow}>
                    <TableCell
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('networkCompany')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Network / Company
                        {renderSortIcon('networkCompany')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('brandName')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Brand Name
                        {renderSortIcon('brandName')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('offerTitle')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Offer
                        {renderSortIcon('offerTitle')}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('clicks')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Clicks
                        {renderSortIcon('clicks')}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('conversions')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Sales
                        {renderSortIcon('conversions')}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('cpa')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        CPA
                        {renderSortIcon('cpa')}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('totalPayout')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Revenue
                        {renderSortIcon('totalPayout')}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Summary row aligned with columns */}
                  <TableRow sx={{ backgroundColor: '#EFF6FF' }}>
                    <TableCell colSpan={3} sx={{ ...tableStyles.bodyCell, fontWeight: 'bold', color: '#1E40AF' }}>
                      TOTAL (ALL OFFERS)
                    </TableCell>
                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, fontWeight: '600', color: '#1E40AF' }}>
                      {offerStatsSummary.totalClicks.toLocaleString()}
                    </TableCell>
                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, fontWeight: '600', color: '#1E40AF' }}>
                      {offerStatsSummary.totalConversions.toLocaleString()}
                    </TableCell>
                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, fontWeight: '600', color: '#1E40AF' }}>
                      ${(offerStatsSummary.averageCPA || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, fontWeight: '600', color: '#1E40AF' }}>
                      ${(offerStatsSummary.totalRevenue || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                  {offerStatsData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={tableStyles.emptyRow}>
                        <Typography variant="body1" color="text.secondary">
                          No data available for the selected filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    getSortedData().map((row, index) => (
                      <TableRow
                        key={index}
                        sx={index % 2 === 0 ? tableStyles.bodyRow : tableStyles.bodyRowAlternate}
                      >
                        <TableCell sx={tableStyles.bodyCell}>
                          {row.networkCompany || 'N/A'}
                        </TableCell>
                        <TableCell sx={tableStyles.bodyCell}>
                          {row.brandName || 'N/A'}
                        </TableCell>
                        <TableCell sx={tableStyles.bodyCell}>
                          {row.offerTitle || 'N/A'}
                        </TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          {row.clicks.toLocaleString()}
                        </TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          {row.conversions.toLocaleString()}
                        </TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          ${row.cpa.toFixed(2)}
                        </TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          ${row.totalPayout.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderAffiliateStats = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupIcon color="primary" />
                <Box>
                  <Typography variant="h4">{affiliateStatsData.totalAffiliates}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Affiliates</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="success" />
                <Box>
                  <Typography variant="h4">{affiliateStatsData.activeAffiliates}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Affiliates</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="info" />
                <Box>
                  <Typography variant="h4">$12,450</Typography>
                  <Typography variant="body2" color="text.secondary">Total Commissions</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="warning" />
                <Box>
                  <Typography variant="h4">238</Typography>
                  <Typography variant="body2" color="text.secondary">Total Conversions</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MockChart title="Affiliate Signups Over Time" type="bar" />
        </Grid>
        <Grid item xs={12} md={6}>
          <MockChart title="Commission Trends" type="line" />
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top Performing Affiliates</Typography>
              <TableContainer component={Paper} sx={tableStyles.container}>
                <Table>
                  <TableHead>
                    <TableRow sx={tableStyles.headerRow}>
                      <TableCell sx={tableStyles.headerCell}>Affiliate</TableCell>
                      <TableCell align="center" sx={tableStyles.headerCell}>Commissions</TableCell>
                      <TableCell align="center" sx={tableStyles.headerCell}>Conversions</TableCell>
                      <TableCell align="center" sx={tableStyles.headerCell}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {affiliateStatsData.topPerformers.map((affiliate, index) => (
                      <TableRow
                        key={index}
                        sx={index % 2 === 0 ? tableStyles.bodyRow : tableStyles.bodyRowAlternate}
                      >
                        <TableCell sx={tableStyles.bodyCell}>{affiliate.name}</TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>{affiliate.commissions}</TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>{affiliate.conversions}</TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          <Chip label="Active" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderBlogFilters = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>View Mode</InputLabel>
              <Select
                value={viewAllAffiliates ? 'all' : 'single'}
                label="View Mode"
                onChange={(e) => {
                  const viewAll = e.target.value === 'all';
                  setViewAllAffiliates(viewAll);
                  if (!viewAll) {
                    setSelectedAffiliateId('');
                  }
                }}
                size="small"
              >
                <MenuItem value="all">All Affiliates</MenuItem>
                <MenuItem value="single">Single Affiliate</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {!viewAllAffiliates && (
            <Grid item>
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel shrink>Affiliate</InputLabel>
                <Select
                  value={selectedAffiliateId}
                  label="Affiliate"
                  displayEmpty
                  notched
                  onChange={(e) => setSelectedAffiliateId(e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select Affiliate</em>
                  </MenuItem>
                  {affiliates.map((affiliate) => (
                    <MenuItem key={affiliate._id} value={affiliate._id}>
                      {affiliate.name || affiliate.email} {affiliate.affiliateData?.name ? `(${affiliate.affiliateData.name})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item>
            <DateRangeFilter
              label="Date Range"
              value={blogPreset}
              size="small"
              onChange={(dates, preset) => {
                setBlogPreset(preset);
                setBlogDateDates(dates);
              }}
            />
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel shrink>Category</InputLabel>
              <Select
                value={blogSelectedCategory}
                label="Category"
                displayEmpty
                notched
                onChange={(e) => {
                  setBlogSelectedCategory(e.target.value);
                  setBlogSelectedSubcategory('');
                }}
                size="small"
              >
                <MenuItem value="">
                  <em>All Categories</em>
                </MenuItem>
                {blogCategories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 200 }} disabled={!blogSelectedCategory}>
              <InputLabel shrink>Subcategory</InputLabel>
              <Select
                value={blogSelectedSubcategory}
                label="Subcategory"
                displayEmpty
                notched
                onChange={(e) => setBlogSelectedSubcategory(e.target.value)}
                size="small"
              >
                <MenuItem value="">
                  <em>All Subcategories</em>
                </MenuItem>
                {blogSelectedCategory && blogCategories.find(c => c._id === blogSelectedCategory)?.subcategories?.map((sub) => (
                  <MenuItem key={sub._id} value={sub._id}>
                    {sub.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {(blogSelectedCategory || blogSelectedSubcategory) && (
            <Grid item>
              <Button
                variant="outlined"
                onClick={() => {
                  setBlogSelectedCategory('');
                  setBlogSelectedSubcategory('');
                }}
                size="small"
              >
                Clear Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const renderBlogStats = () => (
    <Box>
      {/* Filters Section */}
      {renderBlogFilters()}

      {
        blogStatsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : blogStatsError ? (
          <Typography color="error" sx={{ mb: 2 }}>{blogStatsError}</Typography>
        ) : blogStats ? (
          <>
            {/* Overall Blog Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <VisibilityIcon color="primary" />
                      <Box>
                        <Typography variant="h4">{(blogStats.overall?.totalViews || 0).toLocaleString()}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Blog Views</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h4">{(blogStats.overall?.videoPlays || 0).toLocaleString()}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Video Plays</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TrendingUpIcon color="success" />
                      <Box>
                        <Typography variant="h4">{(blogStats.overall?.sales || 0).toLocaleString()}</Typography>
                        <Typography variant="body2" color="text.secondary">Sales</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AttachMoneyIcon color="warning" />
                      <Box>
                        <Typography variant="h4">${(blogStats.overall?.revenue || 0).toFixed(2)}</Typography>
                        <Typography variant="body2" color="text.secondary">Revenue</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AssessmentIcon color="info" />
                      <Box>
                        <Typography variant="h4">{(blogStats.overall?.uniqueSessions || 0).toLocaleString()}</Typography>
                        <Typography variant="body2" color="text.secondary">Unique Sessions</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Blog Performance Table */}
            {(() => {
              const hasHeadSnake = blogStats.head && blogStats.snake;
              const blogs = hasHeadSnake ? blogStats.head.blogs : blogStats.byBlog;
              const pageStatsArray = hasHeadSnake ? blogStats.head.pageStats : blogStats.pageStatsByBlog;
              const sectionTotals = hasHeadSnake ? blogStats.head.totals : null;

              if (!blogs || blogs.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    No blog views found for this period. Views will appear here once blog posts are viewed.
                  </Typography>
                );
              }

              // Group blogs by affiliate for admin view
              let affiliateGroups = [];
              if (viewAllAffiliates) {
                const groupedByAffiliate = new Map();
                blogs.forEach(blog => {
                  const affiliateId = blog.affiliateId || 'unknown';
                  if (!groupedByAffiliate.has(affiliateId)) {
                    groupedByAffiliate.set(affiliateId, {
                      affiliateId: blog.affiliateId,
                      affiliateName: blog.affiliateName,
                      affiliateUsername: blog.affiliateUsername,
                      blogs: [],
                      totalViews: 0,
                      totalVideoPlays: 0,
                      totalClicks: 0,
                      totalSales: 0,
                      totalRevenue: 0
                    });
                  }
                  const group = groupedByAffiliate.get(affiliateId);
                  group.blogs.push(blog);
                  group.totalViews += blog.totalViews || 0;
                  group.totalVideoPlays += blog.videoPlays || 0;
                  group.totalClicks += blog.clicks || 0;
                  group.totalSales += blog.sales || 0;
                  group.totalRevenue += blog.totalRevenue || 0;
                });

                affiliateGroups = Array.from(groupedByAffiliate.values()).sort((a, b) => {
                  let aValue, bValue;
                  switch (blogSortColumn) {
                    case 'affiliateName':
                      aValue = (a.affiliateName || a.affiliateId || '').toLowerCase();
                      bValue = (b.affiliateName || b.affiliateId || '').toLowerCase();
                      break;
                    case 'totalViews':
                      aValue = a.totalViews || 0;
                      bValue = b.totalViews || 0;
                      break;
                    case 'totalSales':
                      aValue = a.totalSales || 0;
                      bValue = b.totalSales || 0;
                      break;
                    case 'totalRevenue':
                      aValue = a.totalRevenue || 0;
                      bValue = b.totalRevenue || 0;
                      break;
                    case 'rpv':
                      aValue = a.totalViews > 0 ? a.totalRevenue / a.totalViews : 0;
                      bValue = b.totalViews > 0 ? b.totalRevenue / b.totalViews : 0;
                      break;
                    default:
                      aValue = (a.affiliateName || a.affiliateId || '').toLowerCase();
                      bValue = (b.affiliateName || b.affiliateId || '').toLowerCase();
                  }

                  const comparison = typeof aValue === 'string'
                    ? aValue.localeCompare(bValue)
                    : aValue - bValue;

                  return blogSortDirection === 'asc' ? comparison : -comparison;
                });
              }

              // Create a mapping from affiliate IDs to simple numbers for display
              const affiliateDisplayIdMap = new Map();
              affiliateGroups.forEach((group, index) => {
                affiliateDisplayIdMap.set(group.affiliateId, (index + 1).toString());
              });

              return (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                        Blog Stats
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {/* Summary moved to table body */}
                      </Box>
                    </Box>
                    <TableContainer component={Paper} sx={tableStyles.container}>
                      <Table size="small" sx={{ tableLayout: 'fixed', width: 1168, minWidth: 1168 }}>
                        <TableHead>
                          <TableRow sx={tableStyles.headerRow}>
                            <TableCell padding="checkbox" sx={{ ...tableStyles.headerCell, width: 48, minWidth: 48 }} />
                            {viewAllAffiliates ? (
                              <TableCell
                                sx={{
                                  ...tableStyles.sortableHeaderCell,
                                  width: 160,
                                  minWidth: 160,
                                }}
                                onClick={() => handleBlogSort('affiliateName')}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  Affiliate
                                  {renderSortIcon('affiliateName', blogSortColumn, blogSortDirection)}
                                </Box>
                              </TableCell>
                            ) : (
                              <TableCell sx={{ ...tableStyles.headerCell, width: 160, minWidth: 160 }} />
                            )}
                            <TableCell sx={{ ...tableStyles.headerCell, width: 100, minWidth: 100 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>Blog ID</Box>
                            </TableCell>
                            <TableCell sx={{ ...tableStyles.headerCell, width: 220, minWidth: 220 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>Blog Title</Box>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...tableStyles.sortableHeaderCell, width: 80, minWidth: 80 }}
                              onClick={() => handleBlogSort('totalViews')}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                Views
                                {renderSortIcon('totalViews', blogSortColumn, blogSortDirection)}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...tableStyles.sortableHeaderCell, width: 80, minWidth: 80 }}
                              onClick={() => handleBlogSort('videoPlays')}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                Video Plays
                                {renderSortIcon('videoPlays', blogSortColumn, blogSortDirection)}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...tableStyles.headerCell, width: 70, minWidth: 70 }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                Clicks
                                {renderSortIcon('totalClicks', blogSortColumn, blogSortDirection)}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...tableStyles.headerCell, width: 70, minWidth: 70 }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                CTR
                                {renderSortIcon('ctr', blogSortColumn, blogSortDirection)}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...tableStyles.sortableHeaderCell, width: 70, minWidth: 70 }}
                              onClick={() => handleBlogSort('totalSales')}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                Sales
                                {renderSortIcon('totalSales', blogSortColumn, blogSortDirection)}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...tableStyles.sortableHeaderCell, width: 100, minWidth: 100 }}
                              onClick={() => handleBlogSort('totalRevenue')}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                Revenue
                                {renderSortIcon('totalRevenue', blogSortColumn, blogSortDirection)}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...tableStyles.headerCell, width: 70, minWidth: 70 }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                CVR
                                {renderSortIcon('cvr', blogSortColumn, blogSortDirection)}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...tableStyles.sortableHeaderCell, width: 90, minWidth: 90 }}
                              onClick={() => handleBlogSort('rpv')}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                RPV
                                {renderSortIcon('rpv', blogSortColumn, blogSortDirection)}
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.headerCell, width: 90, minWidth: 90 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Pages Tracked</Box>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {/* Summary Rows */}
                          {/* Total Row */}
                          <TableRow sx={{ ...tableStyles.groupRow, backgroundColor: '#EFF6FF' }}>
                            <TableCell padding="checkbox" sx={{ ...tableStyles.bodyCell, width: 48, backgroundColor: '#EFF6FF' }} />
                            <TableCell colSpan={3} sx={{ ...tableStyles.bodyCell, width: 480, fontWeight: 'bold', color: '#1E40AF' }}>
                              TOTAL (OVERALL)
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80, fontWeight: '600', color: '#1E40AF' }}>
                              <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{(blogStats.overall?.totalViews || 0).toLocaleString()}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80, fontWeight: '600', color: '#1E40AF' }}>
                              <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{(blogStats.overall?.videoPlays || 0).toLocaleString()}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#1E40AF' }}>
                              <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{(blogStats.overall?.clicks || 0).toLocaleString()}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#1E40AF' }}>
                              <Typography sx={{ fontSize: '0.8rem' }}>
                                {blogStats.overall?.totalViews > 0 ? ((blogStats.overall.clicks || 0) / blogStats.overall.totalViews * 100).toFixed(2) + '%' : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#1E40AF' }}>
                              <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{blogStats.overall?.sales || 0}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 100, fontWeight: '600', color: '#1E40AF' }}>
                              <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">${(blogStats.overall?.revenue || 0).toFixed(2)}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#1E40AF' }}>
                              <Typography sx={{ fontSize: '0.8rem' }}>-</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90, fontWeight: '600', color: '#1E40AF' }}>
                              <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">
                                {blogStats.overall?.totalViews > 0 && !isNaN(blogStats.overall.revenue / blogStats.overall.totalViews)
                                  ? `$${(blogStats.overall.revenue / blogStats.overall.totalViews).toFixed(2)}`
                                  : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90, fontWeight: '600', color: '#1E40AF' }}>
                              <Typography sx={{ fontSize: '0.8rem' }}>-</Typography>
                            </TableCell>
                          </TableRow>

                          {/* Head Row */}
                          <TableRow sx={{ ...tableStyles.groupRow, backgroundColor: '#F0FDF4' }}>
                            <TableCell padding="checkbox" sx={{ ...tableStyles.bodyCell, width: 48, backgroundColor: '#F0FDF4' }} />
                            <TableCell colSpan={7} sx={{ ...tableStyles.bodyCell, width: 770, fontWeight: 'bold', color: '#166534' }}>
                              HEAD REVENUE
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 100, fontWeight: '600', color: '#166534' }}>
                              ${(blogStats.head?.totals?.revenue || 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#166534' }}>
                              {(blogStats.head?.totals?.clicks || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#166534' }}>
                              {blogStats.head?.totals?.views > 0 ? ((blogStats.head.totals.clicks || 0) / blogStats.head.totals.views * 100).toFixed(2) + '%' : '-'}
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90, fontWeight: '600', color: '#166534' }}>-</TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90, fontWeight: '600', color: '#166534' }}>-</TableCell>
                          </TableRow>

                          {/* Snake Row */}
                          <TableRow sx={{ ...tableStyles.groupRow, backgroundColor: '#FFFBEB' }}>
                            <TableCell padding="checkbox" sx={{ ...tableStyles.bodyCell, width: 48, backgroundColor: '#FFFBEB' }} />
                            <TableCell colSpan={7} sx={{ ...tableStyles.bodyCell, width: 770, fontWeight: 'bold', color: '#92400E' }}>
                              SNAKE REVENUE
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 100, fontWeight: '600', color: '#92400E' }}>
                              ${(blogStats.snake?.totals?.revenue || 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#92400E' }}>
                              {(blogStats.snake?.totals?.clicks || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#92400E' }}>
                              {blogStats.snake?.totals?.views > 0 ? ((blogStats.snake.totals.clicks || 0) / blogStats.snake.totals.views * 100).toFixed(2) + '%' : '-'}
                            </TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90, fontWeight: '600', color: '#92400E' }}>-</TableCell>
                            <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90, fontWeight: '600', color: '#92400E' }}>-</TableCell>
                          </TableRow>
                          {viewAllAffiliates && affiliateGroups.length > 0 ? (
                            affiliateGroups.map((affiliateGroup) => {
                              const affiliateId = affiliateGroup.affiliateId || 'unknown';
                              const isAffiliateExpanded = expandedAffiliates[affiliateId];

                              return (
                                <React.Fragment key={`affiliate-${affiliateId}`}>
                                  {/* Affiliate Summary Row */}
                                  <TableRow sx={tableStyles.groupRow}>
                                    <TableCell padding="checkbox" sx={{ ...tableStyles.bodyCell, width: 48 }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleToggleAffiliate(affiliateId)}
                                      >
                                        {isAffiliateExpanded ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                    </TableCell>
                                    <TableCell component="th" scope="row" sx={{ ...tableStyles.bodyCell, width: 160 }}>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                          ID: {affiliateDisplayIdMap.get(affiliateId) || affiliateId}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">
                                          {affiliateGroup.affiliateName || affiliateGroup.affiliateId || 'Unknown Affiliate'}
                                        </Typography>
                                        {affiliateGroup.affiliateUsername && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                            @{affiliateGroup.affiliateUsername}
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...tableStyles.bodyCell, width: 100 }}></TableCell> {/* Empty cell for Blog ID */}
                                    <TableCell component="th" scope="row" sx={{ ...tableStyles.bodyCell, width: 220 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">
                                        {affiliateGroup.blogs.length} Blog{affiliateGroup.blogs.length !== 1 ? 's' : ''}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{affiliateGroup.totalViews.toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{(affiliateGroup.totalVideoPlays || 0).toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{(affiliateGroup.totalClicks || 0).toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }}>
                                        {affiliateGroup.totalViews > 0 ? ((affiliateGroup.totalClicks || 0) / affiliateGroup.totalViews * 100).toFixed(2) + '%' : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{affiliateGroup.totalSales || 0}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 100 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">${(affiliateGroup.totalRevenue || 0).toFixed(2)}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>-</TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">
                                        ${affiliateGroup.totalViews > 0
                                          ? ((affiliateGroup.totalRevenue || 0) / affiliateGroup.totalViews).toFixed(2)
                                          : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90 }}>-</TableCell>
                                  </TableRow>

                                  {/* Blogs for this affiliate */}
                                  {affiliateGroup.blogs.map((blog) => {
                                    const blogPostId = blog.blogPostId?.toString() || blog.blogPostId;
                                    const pageStats = pageStatsArray?.find(b =>
                                      b.blogPostId?.toString() === blogPostId
                                    );
                                    const isBlogExpanded = expandedBlogs[blogPostId];

                                    return (
                                      <React.Fragment key={`blog-${blogPostId}`}>
                                        <TableRow sx={{ ...tableStyles.bodyRow, display: isAffiliateExpanded ? 'table-row' : 'none' }}>
                                          <TableCell padding="checkbox" sx={{ ...tableStyles.bodyCell, width: 48 }}></TableCell>
                                          <TableCell sx={{ ...tableStyles.bodyCell, width: 160 }}></TableCell> {/* Empty cell for Affiliate Name */}
                                          <TableCell sx={{ ...tableStyles.bodyCell, width: 100, maxWidth: 100, overflow: 'hidden' }}>
                                            <Typography sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{blogPostId}</Typography>
                                          </TableCell>
                                          <TableCell component="th" scope="row" sx={{ ...tableStyles.bodyCell, width: 220 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Box sx={{ flex: 1 }}>
                                                <Typography sx={{ fontSize: '0.8rem' }}>
                                                  {/^Blog \S+$/.test(blog.blogTitle) ? '—' : (blog.blogTitle || '—')}
                                                </Typography>
                                              </Box>
                                              {pageStats?.pages && pageStats.pages.length > 0 && (
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleToggleBlog(blogPostId)}
                                                >
                                                  {isBlogExpanded ? <ExpandLess /> : <ExpandMore />}
                                                </IconButton>
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80 }}>
                                            <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{blog.totalViews.toLocaleString()}</Typography>
                                          </TableCell>
                                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80 }}>
                                            <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{(blog.videoPlays || 0).toLocaleString()}</Typography>
                                          </TableCell>
                                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                            <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold" color="text.primary">{(blog.clicks || 0).toLocaleString()}</Typography>
                                          </TableCell>
                                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                            <Typography sx={{ fontSize: '0.8rem' }} color="text.primary">
                                              {blog.totalViews > 0 ? ((blog.clicks || 0) / blog.totalViews * 100).toFixed(2) + '%' : '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                            <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{blog.sales || 0}</Typography>
                                          </TableCell>
                                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 100 }}>
                                            <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">${(blog.totalRevenue || 0).toFixed(2)}</Typography>
                                          </TableCell>
                                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                            <Typography sx={{ fontSize: '0.8rem' }}>-</Typography>
                                          </TableCell>
                                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90 }}>
                                            <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">
                                              ${blog.totalViews > 0
                                                ? ((blog.totalRevenue || 0) / blog.totalViews).toFixed(2)
                                                : '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90 }}>
                                            <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{pageStats?.pages?.length || 0}</Typography>
                                          </TableCell>
                                        </TableRow>

                                        {/* Page breakdown for this blog */}
                                        {pageStats?.pages && pageStats.pages.length > 0 && (
                                          <TableRow sx={{ display: isAffiliateExpanded ? 'table-row' : 'none' }}>
                                            <TableCell
                                              sx={{ pb: 0, pt: 0, px: 0, border: 'none' }}
                                              colSpan={12}
                                            >
                                              <Collapse in={isBlogExpanded} timeout="auto" unmountOnExit>
                                                <Box sx={{ py: 2 }}>
                                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#374151', pl: '88px' }}>
                                                    Page Breakdown:
                                                  </Typography>
                                                  <Table size="small" sx={{ ...nestedTableStyles.container, tableLayout: 'fixed', width: 1248, minWidth: 1248 }}>
                                                    <TableHead>
                                                      <TableRow>
                                                        <TableCell padding="checkbox" sx={{ ...nestedTableStyles.headerCell, width: 48, minWidth: 48 }} />
                                                        <TableCell sx={{ ...nestedTableStyles.headerCell, width: 160, minWidth: 160 }} />
                                                        <TableCell sx={{ ...nestedTableStyles.headerCell, width: 100, minWidth: 100 }}>Page</TableCell>
                                                        <TableCell sx={{ ...nestedTableStyles.headerCell, width: 220, minWidth: 220 }}>Offer & Element</TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 80, minWidth: 80 }}>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            Views
                                                            {renderSortIcon('dummy', '', '')}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 80, minWidth: 80 }}>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            Video Plays
                                                            {renderSortIcon('dummy', '', '')}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            Clicks
                                                            {renderSortIcon('dummy', '', '')}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            CTR
                                                            {renderSortIcon('dummy', '', '')}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            Sales
                                                            {renderSortIcon('dummy', '', '')}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 100, minWidth: 100 }}>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            Revenue
                                                            {renderSortIcon('dummy', '', '')}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            CVR
                                                            {renderSortIcon('dummy', '', '')}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 90, minWidth: 90 }}>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            RPV
                                                            {renderSortIcon('dummy', '', '')}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ ...nestedTableStyles.headerCell, width: 90, minWidth: 90 }} />
                                                      </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                      {(() => {
                                                        const headPages = pageStats.pages.filter(p => !(p.pageNumber === 'Snake' || p.isSnake));
                                                        if (headPages.length === 0) return null;

                                                        const headTotals = headPages.reduce((acc, p) => ({
                                                          views: acc.views + (p.views || 0),
                                                          videoPlays: acc.videoPlays + (p.videoPlays || 0),
                                                          clicks: acc.clicks + (p.clicks || 0),
                                                          sales: acc.sales + (p.sales || 0),
                                                          revenue: acc.revenue + (p.totalRevenue || 0),
                                                        }), { views: 0, videoPlays: 0, clicks: 0, sales: 0, revenue: 0 });

                                                        const headCtr = headTotals.views > 0 ? ((headTotals.clicks / headTotals.views) * 100).toFixed(2) + '%' : '-';
                                                        const headCvr = headTotals.clicks > 0 ? ((headTotals.sales / headTotals.clicks) * 100).toFixed(2) + '%' : '-';
                                                        const headRpv = headTotals.views > 0 ? (headTotals.revenue / headTotals.views).toFixed(2) : '-';

                                                        return (
                                                          <TableRow sx={{ backgroundColor: '#F0FDF4' }}>
                                                            <TableCell padding="checkbox" sx={{ ...nestedTableStyles.bodyCell, width: 48 }}></TableCell>
                                                            <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 160 }}></TableCell>
                                                            <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 100 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>TOTAL HEAD</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 220 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontStyle: 'italic', color: '#166534' }}>Aggregated Head Pages</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 80 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{headTotals.views.toLocaleString()}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 80 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{(headTotals.videoPlays || 0).toLocaleString()}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{headTotals.clicks.toLocaleString()}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{headCtr}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{headTotals.sales.toLocaleString()}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 100 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>${headTotals.revenue.toFixed(2)}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{headCvr}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 90 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>${headRpv}</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 90 }} />
                                                          </TableRow>
                                                        );
                                                      })()}
                                                      {pageStats.pages.map((page) => {
                                                        const isSnake = page.pageNumber === 'Snake' || page.isSnake;
                                                        return (
                                                          <TableRow
                                                            key={isSnake ? 'snake' : page.pageNumber}
                                                            sx={isSnake ? nestedTableStyles.snakeRow : {}}
                                                          >
                                                            <TableCell padding="checkbox" sx={{ ...nestedTableStyles.bodyCell, width: 48 }}></TableCell>
                                                            <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 160 }}></TableCell>
                                                            <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 100 }}>
                                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: isSnake ? 'bold' : 'normal' }}>{isSnake ? 'SNAKE' : `Page ${page.pageNumber}`}</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 220 }}>
                                                              <Box>
                                                                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>{page.offerName || 'No offer'}</Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{page.offerElementName || 'No element'}</Typography>
                                                              </Box>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 80 }}>
                                                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{(page.views || 0).toLocaleString()}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 80 }}>
                                                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{(page.videoPlays || 0).toLocaleString()}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{(page.clicks || 0).toLocaleString()}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                                                {(!page.ctr || page.ctr === 'NaN' || page.ctr === '0.00') ? '-' : `${page.ctr}%`}
                                                              </Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{(page.sales || 0).toLocaleString()}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 100 }}>
                                                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>${(page.totalRevenue || 0).toFixed(2)}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                                                {page.cvr !== undefined && page.cvr !== 'NaN' && page.cvr !== '0.00' ? `${page.cvr}%` : (page.clicks > 0 ? `${((page.sales || 0) / page.clicks * 100).toFixed(2)}%` : '-')}
                                                              </Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 90 }}>
                                                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                                                ${page.views > 0 ? ((page.totalRevenue || 0) / page.views).toFixed(2) : '-'}
                                                              </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 90 }}></TableCell>
                                                          </TableRow>
                                                        );
                                                      })}
                                                    </TableBody>
                                                  </Table>
                                                </Box>
                                              </Collapse>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })
                          ) : (
                            // Render blogs normally (single affiliate view)
                            blogs.map((blog, blogIndex) => {
                              const blogPostId = blog.blogPostId?.toString() || blog.blogPostId;
                              const pageStats = pageStatsArray?.find(b =>
                                b.blogPostId?.toString() === blogPostId
                              );
                              const isExpanded = expandedBlogs[blogPostId];

                              return (
                                <React.Fragment key={blogPostId}>
                                  <TableRow sx={blogIndex % 2 === 0 ? tableStyles.bodyRow : tableStyles.bodyRowAlternate}>
                                    <TableCell padding="checkbox" sx={{ ...tableStyles.bodyCell, width: 48 }}></TableCell>
                                    <TableCell sx={{ ...tableStyles.bodyCell, width: 160 }}></TableCell> {/* Spacer for Affiliate column */}
                                    <TableCell sx={{ ...tableStyles.bodyCell, width: 100, maxWidth: 100, overflow: 'hidden' }}>
                                      <Typography sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{blogPostId}</Typography>
                                    </TableCell>
                                    <TableCell component="th" scope="row" sx={{ ...tableStyles.bodyCell, width: 220 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography sx={{ fontSize: '0.8rem' }}>
                                            {/^Blog \S+$/.test(blog.blogTitle) ? '—' : (blog.blogTitle || '—')}
                                          </Typography>
                                        </Box>
                                        {pageStats?.pages && pageStats.pages.length > 0 && (
                                          <IconButton
                                            size="small"
                                            onClick={() => handleToggleBlog(blogPostId)}
                                          >
                                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                          </IconButton>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{blog.totalViews.toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{(blog.videoPlays || 0).toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }}>-</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }}>-</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{blog.sales || 0}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 100 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">${(blog.totalRevenue || 0).toFixed(2)}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }}>-</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">
                                        ${blog.totalViews > 0
                                          ? ((blog.totalRevenue || 0) / blog.totalViews).toFixed(2)
                                          : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90 }}>
                                      <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">{pageStats?.pages?.length || 0}</Typography>
                                    </TableCell>
                                  </TableRow>
                                  {
                                    pageStats?.pages && pageStats.pages.length > 0 && (
                                      <TableRow>
                                        <TableCell
                                          sx={{ pb: 0, pt: 0, px: 0, border: 'none' }}
                                          colSpan={12}
                                        >
                                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ py: 2 }}>
                                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#374151', pl: '88px' }}>
                                                Page Breakdown:
                                              </Typography>
                                              <Table size="small" sx={{ ...nestedTableStyles.container, tableLayout: 'fixed', width: 1248, minWidth: 1248 }}>
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell padding="checkbox" sx={{ ...nestedTableStyles.headerCell, width: 48, minWidth: 48 }} />
                                                    <TableCell sx={{ ...nestedTableStyles.headerCell, width: 160, minWidth: 160 }} />
                                                    <TableCell sx={{ ...nestedTableStyles.headerCell, width: 100, minWidth: 100 }}>Page</TableCell>
                                                    <TableCell sx={{ ...nestedTableStyles.headerCell, width: 220, minWidth: 220 }}>Offer & Element</TableCell>
                                                    <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 80, minWidth: 80 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        Views
                                                        {renderSortIcon('dummy', '', '')}
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 80, minWidth: 80 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        Video Plays
                                                        {renderSortIcon('dummy', '', '')}
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        Clicks
                                                        {renderSortIcon('dummy', '', '')}
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        CTR
                                                        {renderSortIcon('dummy', '', '')}
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        Sales
                                                        {renderSortIcon('dummy', '', '')}
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 100, minWidth: 100 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        Revenue
                                                        {renderSortIcon('dummy', '', '')}
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        CVR
                                                        {renderSortIcon('dummy', '', '')}
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 90, minWidth: 90 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        RPV
                                                        {renderSortIcon('dummy', '', '')}
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ ...nestedTableStyles.headerCell, width: 90, minWidth: 90 }} />
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {(() => {
                                                    const headPages = pageStats.pages.filter(p => !(p.pageNumber === 'Snake' || p.isSnake));
                                                    if (headPages.length === 0) return null;

                                                    const headTotals = headPages.reduce((acc, p) => ({
                                                      views: acc.views + (p.views || 0),
                                                      videoPlays: acc.videoPlays + (p.videoPlays || 0),
                                                      clicks: acc.clicks + (p.clicks || 0),
                                                      sales: acc.sales + (p.sales || 0),
                                                      revenue: acc.revenue + (p.totalRevenue || 0),
                                                    }), { views: 0, videoPlays: 0, clicks: 0, sales: 0, revenue: 0 });

                                                    const headCtr = headTotals.views > 0 ? ((headTotals.clicks / headTotals.views) * 100).toFixed(2) + '%' : '-';
                                                    const headCvr = headTotals.clicks > 0 ? ((headTotals.sales / headTotals.clicks) * 100).toFixed(2) + '%' : '-';
                                                    const headRpv = headTotals.views > 0 ? (headTotals.revenue / headTotals.views).toFixed(2) : '-';

                                                    return (
                                                      <TableRow sx={{ backgroundColor: '#F0FDF4' }}>
                                                        <TableCell padding="checkbox" sx={{ ...nestedTableStyles.bodyCell, width: 48 }}></TableCell>
                                                        <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 160 }}></TableCell>
                                                        <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 100 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>TOTAL HEAD</Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 220 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontStyle: 'italic', color: '#166534' }}>Aggregated Head Pages</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 80 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{headTotals.views.toLocaleString()}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 80 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{(headTotals.videoPlays || 0).toLocaleString()}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{headTotals.clicks.toLocaleString()}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{headCtr}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{headTotals.sales.toLocaleString()}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 100 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>${headTotals.revenue.toFixed(2)}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>{headCvr}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 90 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#166534' }}>${headRpv}</Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 90 }} />
                                                      </TableRow>
                                                    );
                                                  })()}
                                                  {pageStats.pages.map((page) => {
                                                    const isSnake = page.pageNumber === 'Snake' || page.isSnake;
                                                    return (
                                                      <TableRow
                                                        key={isSnake ? 'snake' : page.pageNumber}
                                                        sx={isSnake ? nestedTableStyles.snakeRow : {}}
                                                      >
                                                        <TableCell padding="checkbox" sx={{ ...nestedTableStyles.bodyCell, width: 48 }}></TableCell>
                                                        <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 160 }}></TableCell>
                                                        <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 100 }}>
                                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: isSnake ? 'bold' : 'normal' }}>{isSnake ? 'SNAKE' : `Page ${page.pageNumber}`}</Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 220 }}>
                                                          <Box>
                                                            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>{page.offerName || 'No offer'}</Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{page.offerElementName || 'No element'}</Typography>
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 80 }}>
                                                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{page.views.toLocaleString()}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 80 }}>
                                                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{(page.videoPlays || 0).toLocaleString()}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{page.clicks?.toLocaleString() || 0}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                                            {(!page.ctr || page.ctr === 'NaN' || page.ctr === '0.00') ? '-' : `${page.ctr}%`}
                                                          </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{page.sales || 0}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 100 }}>
                                                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>${(page.totalRevenue || 0).toFixed(2)}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                                            {page.cvr !== undefined && page.cvr !== 'NaN' && page.cvr !== '0.00' ? `${page.cvr}%` : (page.clicks > 0 ? `${((page.sales || 0) / page.clicks * 100).toFixed(2)}%` : '-')}
                                                          </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 90 }}>
                                                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                                            ${page.views > 0
                                                              ? ((page.totalRevenue || 0) / page.views).toFixed(2)
                                                              : '-'}
                                                          </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 90 }}></TableCell>
                                                      </TableRow>
                                                    );
                                                  })}
                                                </TableBody>
                                              </Table>
                                            </Box>
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                    )
                                  }
                                </React.Fragment>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              );
            })()}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No blog performance data available. Check the browser console for details.
          </Typography>
        )
      }
    </Box >
  );

  const renderSnakeStats = () => (
    <Box>
      {/* Filters Section */}
      {renderBlogFilters()}

      {/* Summary moved to table body */}

      {blogStatsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : blogStatsError ? (
        <Typography color="error" sx={{ mb: 2 }}>{blogStatsError}</Typography>
      ) : blogStats?.snake ? (
        <>
          {/* Overall Snake Stats Cards moved to table body */}

          {/* Snake Stats Table - Grouped by Blog with Expandable Details */}
          {blogStats.snake?.detailedPageStats && blogStats.snake.detailedPageStats.length > 0 && (() => {
            // Group snake pages by blog
            const snakeBlogGroups = {};
            blogStats.snake.detailedPageStats.forEach(page => {
              const key = page.blogPostId;
              if (!snakeBlogGroups[key]) {
                snakeBlogGroups[key] = {
                  blogPostId: page.blogPostId,
                  blogTitle: page.blogTitle,
                  affiliateId: page.affiliateId,
                  affiliateName: page.affiliateName,
                  affiliateUsername: page.affiliateUsername,
                  pages: [],
                  totals: { views: 0, videoPlays: 0, clicks: 0, sales: 0, revenue: 0 }
                };
              }
              snakeBlogGroups[key].pages.push(page);
              snakeBlogGroups[key].totals.views += page.views || 0;
              snakeBlogGroups[key].totals.videoPlays += page.videoPlays || 0;
              snakeBlogGroups[key].totals.clicks += page.clicks || 0;
              snakeBlogGroups[key].totals.sales += page.sales || 0;
              snakeBlogGroups[key].totals.revenue += page.totalRevenue || 0;
            });

            const groupedBlogs = Object.values(snakeBlogGroups).sort((a, b) => {
              let aValue, bValue;
              switch (snakeSortColumn) {
                case 'affiliateName':
                  aValue = (a.affiliateName || '').toLowerCase();
                  bValue = (b.affiliateName || '').toLowerCase();
                  break;
                case 'blogTitle':
                  aValue = (a.blogTitle || '').toLowerCase();
                  bValue = (b.blogTitle || '').toLowerCase();
                  break;
                case 'views':
                  aValue = a.totals.views || 0;
                  bValue = b.totals.views || 0;
                  break;
                case 'videoPlays':
                  aValue = a.totals.videoPlays || 0;
                  bValue = b.totals.videoPlays || 0;
                  break;
                case 'clicks':
                  aValue = a.totals.clicks || 0;
                  bValue = b.totals.clicks || 0;
                  break;
                case 'ctr':
                  aValue = a.totals.views > 0 ? a.totals.clicks / a.totals.views : 0;
                  bValue = b.totals.views > 0 ? b.totals.clicks / b.totals.views : 0;
                  break;
                case 'sales':
                  aValue = a.totals.sales || 0;
                  bValue = b.totals.sales || 0;
                  break;
                case 'revenue':
                  aValue = a.totals.revenue || 0;
                  bValue = b.totals.revenue || 0;
                  break;
                case 'rpv':
                  aValue = a.totals.views > 0 ? a.totals.revenue / a.totals.views : 0;
                  bValue = b.totals.views > 0 ? b.totals.revenue / b.totals.views : 0;
                  break;
                default:
                  aValue = (a.affiliateName || '').toLowerCase();
                  bValue = (b.affiliateName || '').toLowerCase();
              }

              const comparison = typeof aValue === 'string'
                ? aValue.localeCompare(bValue)
                : aValue - bValue;

              return snakeSortDirection === 'asc' ? comparison : -comparison;
            });

            return (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Snake Page Details by Blog
                  </Typography>
                  <TableContainer component={Paper} sx={tableStyles.container}>
                    <Table size="small" sx={{ tableLayout: 'fixed', width: snakeAdminRevExpanded ? 1418 : 1338, minWidth: snakeAdminRevExpanded ? 1418 : 1338 }}>
                      <TableHead>
                        <TableRow sx={tableStyles.headerRow}>
                          <TableCell padding="checkbox" sx={{ ...tableStyles.headerCell, width: 48, minWidth: 48 }} />
                          <TableCell
                            sx={{
                              ...tableStyles.sortableHeaderCell,
                              width: 160,
                              minWidth: 160
                            }}
                            onClick={() => handleSnakeSort('affiliateName')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              Affiliate
                              {renderSortIcon('affiliateName', snakeSortColumn, snakeSortDirection)}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...tableStyles.headerCell, width: 100, minWidth: 100 }}>Blog ID</TableCell>
                          <TableCell
                            sx={{
                              ...tableStyles.sortableHeaderCell,
                              width: 220,
                              minWidth: 220
                            }}
                            onClick={() => handleSnakeSort('blogTitle')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              Blog
                              {renderSortIcon('blogTitle', snakeSortColumn, snakeSortDirection)}
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ ...tableStyles.sortableHeaderCell, width: 80, minWidth: 80 }}
                            onClick={() => handleSnakeSort('views')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              Views
                              {renderSortIcon('views', snakeSortColumn, snakeSortDirection)}
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ ...tableStyles.sortableHeaderCell, width: 80, minWidth: 80 }}
                            onClick={() => handleSnakeSort('videoPlays')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              Video Plays
                              {renderSortIcon('videoPlays', snakeSortColumn, snakeSortDirection)}
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ ...tableStyles.sortableHeaderCell, width: 70, minWidth: 70 }}
                            onClick={() => handleSnakeSort('clicks')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              Clicks
                              {renderSortIcon('clicks', snakeSortColumn, snakeSortDirection)}
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ ...tableStyles.sortableHeaderCell, width: 70, minWidth: 70 }}
                            onClick={() => handleSnakeSort('ctr')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              CTR
                              {renderSortIcon('ctr', snakeSortColumn, snakeSortDirection)}
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ ...tableStyles.sortableHeaderCell, width: 70, minWidth: 70 }}
                            onClick={() => handleSnakeSort('sales')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              Sales
                              {renderSortIcon('sales', snakeSortColumn, snakeSortDirection)}
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ ...tableStyles.sortableHeaderCell, width: 90, minWidth: 90 }}
                            onClick={() => handleSnakeSort('revenue')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              Total Rev
                              {renderSortIcon('revenue', snakeSortColumn, snakeSortDirection)}
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ ...tableStyles.headerCell, width: snakeAdminRevExpanded ? 180 : 100, minWidth: snakeAdminRevExpanded ? 180 : 100 }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              {snakeAdminRevExpanded ? (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                  <Box sx={{ width: 80, textAlign: 'center' }}>Admin Rev</Box>
                                  <Box sx={{ width: 80, textAlign: 'center', color: '#6B7280' }}>Aff Rev</Box>
                                </Box>
                              ) : (
                                <Box>Admin Rev</Box>
                              )}
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSnakeAdminRevExpanded(!snakeAdminRevExpanded);
                                }}
                                sx={{ p: 0 }}
                              >
                                {snakeAdminRevExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.headerCell, width: 70, minWidth: 70 }}>CVR</TableCell>
                          <TableCell
                            align="center"
                            sx={{ ...tableStyles.sortableHeaderCell, width: 90, minWidth: 90 }}
                            onClick={() => handleSnakeSort('rpv')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              RPV
                              {renderSortIcon('rpv', snakeSortColumn, snakeSortDirection)}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.headerCell, width: 90, minWidth: 90 }}>Pages</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Summary row aligned with columns */}
                        <TableRow sx={{ backgroundColor: '#FFFBEB' }}>
                          <TableCell padding="checkbox" sx={{ ...tableStyles.bodyCell, width: 48 }}></TableCell>
                          <TableCell colSpan={3} sx={{ ...tableStyles.bodyCell, width: 480, fontWeight: 'bold', color: '#92400E' }}>
                            TOTAL (ALL SNAKE)
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80, fontWeight: '600', color: '#92400E' }}>
                            {(blogStats.snake?.totals?.views || 0).toLocaleString()}
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80, fontWeight: '600', color: '#92400E' }}>
                            {(blogStats.snake?.totals?.videoPlays || 0).toLocaleString()}
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#92400E' }}>
                            {(blogStats.snake?.totals?.clicks || 0).toLocaleString()}
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#92400E' }}>
                            {blogStats.snake?.totals?.views > 0
                              ? ((blogStats.snake.totals.clicks / blogStats.snake.totals.views) * 100).toFixed(2) + '%'
                              : '-'}
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#92400E' }}>
                            {blogStats.snake?.totals?.sales || 0}
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90, fontWeight: '600', color: '#92400E' }}>
                            ${(blogStats.snake?.totals?.revenue || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: snakeAdminRevExpanded ? 180 : 100, fontWeight: '600', color: '#92400E' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: snakeAdminRevExpanded ? 2 : 0 }}>
                              <Box sx={{ width: snakeAdminRevExpanded ? 80 : 'auto', textAlign: 'center' }}>
                                ${((blogStats.snake?.totals?.revenue || 0) * 0.3).toFixed(2)}
                              </Box>
                              {snakeAdminRevExpanded && (
                                <Box sx={{ width: 80, textAlign: 'center', color: '#B45309' }}>
                                  ${((blogStats.snake?.totals?.revenue || 0) * 0.7).toFixed(2)}
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70, fontWeight: '600', color: '#92400E' }}>-</TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90, fontWeight: '600', color: '#92400E' }}>
                            ${blogStats.snake?.totals?.views > 0
                              ? (blogStats.snake.totals.revenue / blogStats.snake.totals.views).toFixed(2)
                              : '-'}
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90 }}>-</TableCell>
                        </TableRow>
                        {groupedBlogs.map((blogGroup) => {
                          const isExpanded = expandedSnakeBlogs[blogGroup.blogPostId];
                          const ctr = blogGroup.totals.views > 0
                            ? ((blogGroup.totals.clicks / blogGroup.totals.views) * 100).toFixed(2) + '%'
                            : '-';
                          const rpv = blogGroup.totals.views > 0
                            ? (blogGroup.totals.revenue / blogGroup.totals.views).toFixed(2)
                            : '-';

                          return (
                            <React.Fragment key={blogGroup.blogPostId}>
                              {/* Blog Summary Row */}
                              <TableRow
                                sx={{
                                  ...tableStyles.bodyRow,
                                  cursor: 'pointer',
                                }}
                                onClick={() => setExpandedSnakeBlogs(prev => ({
                                  ...prev,
                                  [blogGroup.blogPostId]: !prev[blogGroup.blogPostId]
                                }))}
                              >
                                <TableCell padding="checkbox" sx={{ ...tableStyles.bodyCell, width: 48 }}>
                                  <IconButton size="small">
                                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ ...tableStyles.bodyCell, width: 160 }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      {blogGroup.affiliateName || 'Unknown'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      @{blogGroup.affiliateUsername || 'N/A'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ ...tableStyles.bodyCell, width: 100 }}>
                                  <Typography sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{blogGroup.blogPostId}</Typography>
                                </TableCell>
                                <TableCell sx={{ ...tableStyles.bodyCell, width: 220 }}>
                                  <Box>
                                    <Typography sx={{ fontSize: '0.8rem' }} fontWeight="bold">
                                      {/^Blog \S+$/.test(blogGroup.blogTitle) ? '—' : (blogGroup.blogTitle || '—')}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80 }}>
                                  <Typography fontWeight="bold">{blogGroup.totals.views.toLocaleString()}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 80 }}>
                                  <Typography fontWeight="bold">{(blogGroup.totals.videoPlays || 0).toLocaleString()}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                  <Typography fontWeight="bold">{blogGroup.totals.clicks.toLocaleString()}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                  <Typography fontWeight="bold">{ctr}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>
                                  <Typography fontWeight="bold">{blogGroup.totals.sales}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90 }}>
                                  <Typography fontWeight="bold">${blogGroup.totals.revenue.toFixed(2)}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: snakeAdminRevExpanded ? 180 : 100 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: snakeAdminRevExpanded ? 2 : 0 }}>
                                    <Box sx={{ width: snakeAdminRevExpanded ? 80 : 'auto', textAlign: 'center' }}>
                                      <Typography fontWeight="bold">${(blogGroup.totals.revenue * 0.3).toFixed(2)}</Typography>
                                    </Box>
                                    {snakeAdminRevExpanded && (
                                      <Box sx={{ width: 80, textAlign: 'center' }}>
                                        <Typography fontWeight="bold" color="text.secondary">${(blogGroup.totals.revenue * 0.7).toFixed(2)}</Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 70 }}>-</TableCell>
                                <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90 }}>
                                  <Typography fontWeight="bold">${rpv}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ ...tableStyles.bodyCell, width: 90 }}>
                                  <Typography fontWeight="bold">{blogGroup.pages.length}</Typography>
                                </TableCell>
                              </TableRow>

                              {/* Expanded Page Details */}
                              {isExpanded && (
                                <TableRow>
                                  <TableCell sx={{ pb: 0, pt: 0, px: 0, border: 'none' }} colSpan={12}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ py: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#374151', pl: '88px' }}>
                                          Page Breakdown:
                                        </Typography>
                                        <Table size="small" sx={{ ...nestedTableStyles.container, tableLayout: 'fixed', width: snakeAdminRevExpanded ? 1418 : 1338, minWidth: snakeAdminRevExpanded ? 1418 : 1338 }}>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell padding="checkbox" sx={{ ...nestedTableStyles.headerCell, width: 48, minWidth: 48 }} />
                                              <TableCell sx={{ ...nestedTableStyles.headerCell, width: 160, minWidth: 160 }} />
                                              <TableCell sx={{ ...nestedTableStyles.headerCell, width: 100, minWidth: 100 }}>Page</TableCell>
                                              <TableCell sx={{ ...nestedTableStyles.headerCell, width: 220, minWidth: 220 }}>Offer & Element</TableCell>
                                              <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 80, minWidth: 80 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  Views
                                                  {renderSortIcon('dummy', '', '')}
                                                </Box>
                                              </TableCell>
                                              <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 80, minWidth: 80 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  Video Plays
                                                  {renderSortIcon('dummy', '', '')}
                                                </Box>
                                              </TableCell>
                                              <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  Clicks
                                                  {renderSortIcon('dummy', '', '')}
                                                </Box>
                                              </TableCell>
                                              <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  CTR
                                                  {renderSortIcon('dummy', '', '')}
                                                </Box>
                                              </TableCell>
                                              <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  Sales
                                                  {renderSortIcon('dummy', '', '')}
                                                </Box>
                                              </TableCell>
                                              <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 90, minWidth: 90 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  Total Rev
                                                  {renderSortIcon('dummy', '', '')}
                                                </Box>
                                              </TableCell>
                                              <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: snakeAdminRevExpanded ? 180 : 100, minWidth: snakeAdminRevExpanded ? 180 : 100 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  {snakeAdminRevExpanded ? (
                                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                                      <Box sx={{ width: 80, textAlign: 'center' }}>Admin Rev</Box>
                                                      <Box sx={{ width: 80, textAlign: 'center' }}>Aff Rev</Box>
                                                    </Box>
                                                  ) : (
                                                    'Admin Rev'
                                                  )}
                                                </Box>
                                              </TableCell>
                                              <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 70, minWidth: 70 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  CVR
                                                  {renderSortIcon('dummy', '', '')}
                                                </Box>
                                              </TableCell>
                                              <TableCell align="center" sx={{ ...nestedTableStyles.headerCell, width: 90, minWidth: 90 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  RPV
                                                  {renderSortIcon('dummy', '', '')}
                                                </Box>
                                              </TableCell>
                                              <TableCell sx={{ ...nestedTableStyles.headerCell, width: 90, minWidth: 90 }} />
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {blogGroup.pages.map((page, pageIndex) => (
                                              <TableRow key={pageIndex}>
                                                <TableCell padding="checkbox" sx={{ ...nestedTableStyles.bodyCell, width: 48 }}></TableCell>
                                                <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 160 }}></TableCell>
                                                <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 100 }}>
                                                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: (page.pageNumber === 'Snake' || page.isSnake) ? 'bold' : 'normal' }}>
                                                    {(page.pageNumber === 'Snake' || page.isSnake) ? 'SNAKE' : `Page ${page.pageNumber}`}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 220 }}>
                                                  <Box>
                                                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>{page.offerName || 'No offer'}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{page.offerElementName || 'No element'}</Typography>
                                                  </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 80 }}>
                                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{(page.views || 0).toLocaleString()}</Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 80 }}>
                                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{(page.videoPlays || 0).toLocaleString()}</Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{(page.clicks || 0).toLocaleString()}</Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                                    {(!page.ctr || page.ctr === 'NaN' || page.ctr === '0.00') ? '-' : `${page.ctr}%`}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>{page.sales || 0}</Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 90 }}>
                                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>${(page.totalRevenue || 0).toFixed(2)}</Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: snakeAdminRevExpanded ? 180 : 100 }}>
                                                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: snakeAdminRevExpanded ? 2 : 0 }}>
                                                    <Box sx={{ width: snakeAdminRevExpanded ? 80 : 'auto', textAlign: 'center' }}>
                                                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>${((page.totalRevenue || 0) * 0.3).toFixed(2)}</Typography>
                                                    </Box>
                                                    {snakeAdminRevExpanded && (
                                                      <Box sx={{ width: 80, textAlign: 'center' }}>
                                                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>${((page.totalRevenue || 0) * 0.7).toFixed(2)}</Typography>
                                                      </Box>
                                                    )}
                                                  </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 70 }}>
                                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                                    {page.cvr !== undefined && page.cvr !== 'NaN' && page.cvr !== '0.00' ? `${page.cvr}%` : (page.clicks > 0 ? `${((page.sales || 0) / page.clicks * 100).toFixed(2)}%` : '-')}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...nestedTableStyles.bodyCell, width: 90 }}>
                                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                                    ${page.views > 0 ? ((page.totalRevenue || 0) / page.views).toFixed(2) : '-'}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell sx={{ ...nestedTableStyles.bodyCell, width: 90 }}></TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
          })()}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No snake stats available. Snake stats appear when blogs have merged content from other affiliates.
        </Typography>
      )}
    </Box>
  );

  const renderCategoryStats = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CategoryIcon color="primary" />
                <Box>
                  <Typography variant="h4">28</Typography>
                  <Typography variant="body2" color="text.secondary">Total Categories</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="success" />
                <Box>
                  <Typography variant="h4">24</Typography>
                  <Typography variant="body2" color="text.secondary">Active Categories</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="info" />
                <Box>
                  <Typography variant="h4">156</Typography>
                  <Typography variant="body2" color="text.secondary">Total Offers</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="warning" />
                <Box>
                  <Typography variant="h4">5.6</Typography>
                  <Typography variant="body2" color="text.secondary">Avg Offers/Category</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <MockChart title="Category Performance" type="bar" />
        </Grid>
        <Grid item xs={12} md={4}>
          <MockPieChart title="Category Distribution" data={categoryData} />
        </Grid>
      </Grid>
    </Box>
  );

  const renderPlatformSettings = () => (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Global Identifiers & APIs
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Connect your Admin Google AdSense Account to automate revenue reporting across all affiliates.
              The server will save a background token so no one else has to log in.
            </Typography>
            {adsenseSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Global AdSense connection was successful! The token is saved securely.
              </Alert>
            )}

            {!checkingAdsenseStatus && adsenseGlobalConnected && !adsenseSuccess && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Global AdSense is already connected and live{adsenseGlobalEmail ? ` (${adsenseGlobalEmail})` : ''}.
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              disabled={adsenseGlobalConnected || checkingAdsenseStatus}
              onClick={async () => {
                try {
                  const res = await api.get('/adsense/setup');
                  if (res.data && res.data.setupUrl) {
                    window.location.href = res.data.setupUrl;
                  }
                } catch (e) {
                  alert('Failed to get setup URL');
                }
              }}
              sx={{ mr: 2 }}
            >
              {adsenseGlobalConnected ? 'Connected' : 'Connect Global AdSense Account'}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={async () => {
                setAdsensePingLoading(true);
                setAdsensePingResult(null);
                try {
                  const res = await api.get('/adsense/revenue');
                  setAdsensePingResult({ success: true, data: res.data });
                } catch (e) {
                  setAdsensePingResult({ success: false, error: e.response?.data?.error || e.message });
                } finally {
                  setAdsensePingLoading(false);
                }
              }}
              disabled={adsensePingLoading}
            >
              {adsensePingLoading ? <CircularProgress size={24} /> : 'Test AdSense API'}
            </Button>

            {adsensePingResult && (
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  API Test Result: {adsensePingResult.success ? '✅ Success' : '❌ Failed'}
                </Typography>
                <pre style={{ margin: 0, overflowX: 'auto', fontSize: '12px' }}>
                  {JSON.stringify(adsensePingResult.data || adsensePingResult.error, null, 2)}
                </pre>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const tabContent = [
    renderOfferStats,
    renderAffiliateStats,
    renderBlogStats,
    renderSnakeStats,
    renderCategoryStats,
    renderPlatformSettings
  ];

  return (
    <DashboardLayout title="Reporting">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Comprehensive analytics and reporting dashboard
          </Typography>
        </Box>

        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Offer Stats" />
            <Tab label="Affiliate Stats" sx={{ display: 'none' }} />
            <Tab label="Blog Stats" />
            <Tab label="Snake Stats" />
            <Tab label="Category Stats" sx={{ display: 'none' }} />
            <Tab label="Platform Settings" sx={{ display: 'none' }} />
          </Tabs>
        </Paper>

        <Box sx={{ py: 3 }}>
          {tabContent[activeTab]()}
        </Box>
      </Container>
    </DashboardLayout>
  );
};

export default Reporting; 